/**
 * Color utility functions
 */

/**
 * Calculate luminance of a color
 * Uses the relative luminance formula from WCAG 2.0
 * @param hex - Hex color string (e.g., "#DB3028" or "DB3028")
 * @returns Luminance value between 0 and 1
 */
function getLuminance(hex: string): number {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const gammaCorrect = (value: number) =>
    value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);

  const rLinear = gammaCorrect(r);
  const gLinear = gammaCorrect(g);
  const bLinear = gammaCorrect(b);

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Get contrasting text color (black or white) for a given background color
 * @param backgroundColor - Hex color string (e.g., "#DB3028")
 * @returns "#FFFFFF" for white text or "#000000" for black text
 */
export function getContrastingTextColor(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);

  // Choose text color (black or white) based on WCAG contrast ratios
  // Contrast ratio formula: (L1 + 0.05) / (L2 + 0.05)
  // where L1 is the lighter luminance and L2 is the darker luminance.
  const contrastWithBlack = (luminance + 0.05) / 0.05; // black text has luminance 0
  const contrastWithWhite = 1.05 / (luminance + 0.05); // white text has luminance 1

  return contrastWithBlack >= contrastWithWhite ? '#000000' : '#FFFFFF';
}
